import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchUserProfile } from "../../services/newUserService";
import allImages from "../../assets/images-import";
import { handleErrors } from "../../utils/errorHandler";
import { Loading } from "../Common/OtherElements/Loading";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";
import {
  formatProfileDate,
  formatProfileDateTime,
  normalizeProfileResult,
  resolveProfileImageUrl,
} from "../../utils/profileUtils";

const ProfileDetail = ({ label, value }) => (
  <div className="col-md-6 col-lg-4">
    <div className="mb-3">
      <div className="xc-profile-detail-label">{label}</div>
      <p className="xc-profile-detail-value">{value || "-"}</p>
    </div>
  </div>
);

export const ProfilePageContent = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const response = await fetchUserProfile();
        if (cancelled) return;

        if (response?.data?.isSuccess === false) {
          setLoadError(
            response.data.message || "Unable to load profile information.",
          );
          setProfile(null);
          return;
        }

        const normalized = normalizeProfileResult(response?.data?.result);
        if (!normalized) {
          setLoadError("Profile data is not available.");
          setProfile(null);
          return;
        }

        setProfile(normalized);
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 401) {
          setLoadError("Your session has expired. Please sign in again.");
        } else {
          handleErrors(error);
          setLoadError(
            error?.response?.data?.message ||
              "Unable to load profile. Please try again later.",
          );
        }
        setProfile(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const avatarSrc = profile?.profileImage
    ? resolveProfileImageUrl(profile.profileImage)
    : null;

  return (
    <>
      <ComponentHeader title="My Profile" />

      {loading ? (
        <Loading />
      ) : loadError ? (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger mb-0" role="alert">
              {loadError}
            </div>
          </div>
        </div>
      ) : !profile ? (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-warning mb-0" role="alert">
              No profile information found.
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                <h5 className="mb-0">Profile Details</h5>
                <Link
                  to="/change-password"
                  className="btn btn-sm btn-soft-primary"
                >
                  <i className="mdi mdi-lock me-1"></i>
                  Change Password
                </Link>
              </div>
              <div className="card-body p-4">
                <div className="xc-profile-hero">
                  <img
                    src={avatarSrc || allImages.defaultprofile}
                    className="xc-profile-avatar"
                    alt={profile.userName || "Profile"}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = allImages.defaultprofile;
                    }}
                  />
                  <div>
                    <h5>{profile.userName || "User"}</h5>
                    <p>
                      {profile.roleName || "Admin"}
                      {profile.emailId ? ` · ${profile.emailId}` : ""}
                    </p>
                  </div>
                </div>

                <div className="row">
                  <ProfileDetail label="User Name" value={profile.userName} />
                  <ProfileDetail label="Role Name" value={profile.roleName} />
                  <ProfileDetail label="Email" value={profile.emailId} />
                  <ProfileDetail
                    label="Contact Number"
                    value={profile.contactNo}
                  />
                  <ProfileDetail
                    label="Date Of Joining"
                    value={formatProfileDate(profile.doj)}
                  />
                  <ProfileDetail
                    label="Registered On"
                    value={formatProfileDateTime(profile.registeredOn)}
                  />
                  <ProfileDetail
                    label="Last Login Time"
                    value={formatProfileDateTime(profile.log_time)}
                  />
                  <ProfileDetail label="Last Login IP" value={profile.log_ip} />
                  <ProfileDetail label="Status" value={profile.status} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
